import { useEffect, useState, useCallback } from "react";
import apiClient from "../api/apiClient";
import apiPaths from "../api/endpoints";

interface Student {
  id: string;
  name: string;
}

interface AssignedStory {
  id: string;
  order: number;
}

interface Group {
  id: string;
  name: string;
  students: Student[];
  assignedStories: AssignedStory[];
  updatedAt: string;
}

interface GroupsResponse {
  schoolId: string;
  classId: string;
  defaultGroup: Group;
  customGroups: Group[];
}

interface UseGetGroupsParams {
  schoolHash: string;
  grade: string;
  enabled?: boolean;
}

export function useGetGroups({ schoolHash, grade, enabled = true }: UseGetGroupsParams) {
  const [data, setData] = useState<GroupsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGroups = useCallback(async () => {
    if (!schoolHash || !grade || !enabled) return;

    try {
      setLoading(true);
      setError(null);

      // Construct URL with path parameters: /groups/class/<SCHOOL_HASH>/<GRADE>
      const endpoint = `${apiPaths.GET_GROUPS}/${schoolHash}/${grade}`;
      
      const res = await apiClient.get(endpoint);

      setData(res.data);
    } catch (err: any) {
      console.error("GET_GROUPS API error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch groups"
      );
    } finally {
      setLoading(false);
    }
  }, [schoolHash, grade, enabled]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  return {
    data,
    loading,
    error,
    refetch: fetchGroups,
    // Helper methods to access specific data
    defaultGroup: data?.defaultGroup,
    customGroups: data?.customGroups || [],
    allGroups: data ? [data.defaultGroup, ...data.customGroups] : [],
  };
}

export async function createGroup(
  schoolHash: string,
  classId: string,
  groupName: string,
  students: Array<{ id: string; name: string }>
) {
  try {
    const body = {
      schoolHash,
      classId,
      name: groupName,
      type: "custom",
      students
    };

    const res = await apiClient.post(apiPaths.CREATE_GROUP, body);
    return res.data;
  } catch (err: any) {
    console.error("CREATE_GROUP API error:", err);
    throw new Error(
      err?.response?.data?.message ||
        err?.message ||
        "Failed to create group"
    );
  }
}

export async function updateGroup(
  schoolHash: string,
  classId: string,
  groupName: string,
  students: Array<{ id: string; name: string }>
) {
  try {
    const body = {
      schoolHash,
      classId,
      name: groupName,
      type: "custom",
      students
    };

    const res = await apiClient.post(apiPaths.CREATE_GROUP, body);
    return res.data;
  } catch (err: any) {
    console.error("UPDATE_GROUP API error:", err);
    throw new Error(
      err?.response?.data?.message ||
        err?.message ||
        "Failed to update group"
    );
  }
}